/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import { flatMap, keyBy, union } from 'lodash';

import { Grid, InlineNotification, PageGridContainer } from '@coursera/cds-core';

import { Heading, Section } from 'bundles/enterprise-legacy-learner-home/components/AutoHeading';
import SkillSetsContainerV2 from 'bundles/enterprise-legacy-learner-home/components/single-program/SkillSetsContainerV2';
import filterExistsOrDefault from 'bundles/enterprise-legacy-learner-home/utils/filterExistsOrDefault';
import MultiProgramSkillsetWarningBanner from 'bundles/program-home/components/enterprise-home/MultiProgramSkillsetWarningBanner';
import type {
  ProgramSkillsetsQuery as ProgramSkillsetsQueryData,
  ProgramSkillsetsQueryVariables,
} from 'bundles/program-home/components/enterprise-home/__generated__/ProgramSkillsetsQuery';
import type { EnterpriseProgramFieldsFragment as EnterpriseProgram } from 'bundles/program-home/queries/__generated__/EnterpriseHomePageQuery';

import _t from 'i18n!nls/program-home';

type PropsFromCaller = {
  userId: number;
  thirdPartyOrganizationId: string;
  enterprisePrograms: EnterpriseProgram[];
};

export type Props = PropsFromCaller;

export const ProgramThirdPartyOrgTargetSkillProfileQuery = gql`
  query ProgramSkillsetsQuery($ids: [String!]!) {
    EnterpriseTargetSkillProfileSummariesV1Resource {
      multiGet(ids: $ids) {
        elements {
          id
          slug
          title
          description {
            ... on EnterpriseTargetSkillProfileSummariesV1_cmlMember {
              cml {
                dtdId
                value
              }
            }
          }
          targetSkillProficiencies {
            skillId
            targetProficiency
            skillName
          }
        }
      }
    }
  }
`;

export const MultiProgramSkillSetTabContent = ({ userId, thirdPartyOrganizationId, enterprisePrograms }: Props) => {
  const styles = {
    container: css`
      margin: 2.75rem auto 0;
      padding: 0 var(--cds-spacing-150) 2.75rem;
    `,
    programs: css`
      margin-top: var(--cds-spacing-600);
    `,
    programTitle: css`
      border-bottom: 1px solid var(--cds-color-grey-300);
      padding-bottom: var(--cds-spacing-150);
      margin-bottom: var(--cds-spacing-200);
    `,
    programContent: css`
      margin-top: var(--cds-spacing-600);
    `,
  };

  const { data, error, loading } = useQuery<ProgramSkillsetsQueryData, ProgramSkillsetsQueryVariables>(
    ProgramThirdPartyOrgTargetSkillProfileQuery,
    {
      ssr: false,
      variables: {
        ids: union(
          flatMap(enterprisePrograms, (x) => x.metadata.linkedTargetSkillProfileIds?.filter((id) => id !== null) || [])
        ),
      },
    }
  );

  const skillSetById = keyBy(
    filterExistsOrDefault(data?.EnterpriseTargetSkillProfileSummariesV1Resource?.multiGet?.elements),
    (x) => x.id
  );

  if (error) {
    return (
      <PageGridContainer className="rc-MultiProgramSkillSetTabContent" css={styles.container}>
        <Grid item xs={12}>
          <InlineNotification severity="error">
            {_t('Sorry! Something went wrong. Please refresh the page.')}
          </InlineNotification>
        </Grid>
      </PageGridContainer>
    );
  }

  return (
    <PageGridContainer className="rc-MultiProgramSkillSetTabContent" css={styles.container}>
      <Grid item xs={12}>
        <MultiProgramSkillsetWarningBanner userId={userId} thirdPartyOrganizationId={thirdPartyOrganizationId} />
        {enterprisePrograms
          .filter((enterpriseProgram) => {
            const ids = enterpriseProgram.metadata.linkedTargetSkillProfileIds?.filter((id) => id !== null) || [];
            return ids.length > 0 && ids.some((id) => skillSetById?.[id]);
          })
          .map((enterpriseProgram) => {
            const skillsets = (
              enterpriseProgram.metadata.linkedTargetSkillProfileIds?.filter((id) => id !== null) || []
            )
              .map((id) => skillSetById?.[id])
              .filter(Boolean);

            return (
              <div key={enterpriseProgram.id} css={styles.programs}>
                <Heading defaultLevel={2} css={styles.programTitle}>
                  {enterpriseProgram.metadata.name}
                </Heading>
                <Section initialLevel={3}>
                  <div css={styles.programContent}>
                    <SkillSetsContainerV2
                      skillsets={skillsets}
                      skillSetLength={skillsets.length}
                      programSlug={enterpriseProgram.metadata.slug}
                      programId={enterpriseProgram.id}
                      loading={loading || !skillSetById}
                    />
                  </div>
                </Section>
              </div>
            );
          })}
      </Grid>
    </PageGridContainer>
  );
};

export default MultiProgramSkillSetTabContent;
